/* eslint-disable no-console */
import { inject, Service } from '@angular/core';
import { __ } from '@app/shared/functions/object.functions';
import { ITenantService } from '@app/shared/services/itenant.service';

import { OAuthService } from 'angular-oauth2-oidc';
import {
  fallbackEndSessionUrl,
  OAuthErrorRedirect,
  readOAuthErrorRedirect,
  removeOAuthErrorParameters,
} from '../oauth-error-redirect';
import { clearTransientLoginKeys } from '../oauth-storage';
import { HYPECAST_OAUTH_CONFIG } from '../tokens/hypecast-oauth-config.token';
import { HypecastAuthenticationService } from './authentication.service';
import { AuthFailureService } from './auth-failure.service';

export function removeIssQueryParameter(href: string): string {
  const url = new URL(href);
  if (!url.searchParams.has('iss')) {
    return href;
  }
  url.searchParams.delete('iss');
  return url.toString();
}

@Service()
export class HypecastOAuthService {
  /**
   * Marks the authorization-code callback as already retried once, so a genuinely
   * broken login cannot bounce between app and identity provider forever. Lives in
   * sessionStorage because the retry crosses a full page navigation.
   */
  private static readonly CALLBACK_RETRY_KEY = 'hypecast-auth-callback-retry';

  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** The login chain currently running, so concurrent callers join it instead of starting a second one. */
  private loginInFlight: Promise<unknown> | null = null;

  /** Whether a redirect to the identity provider has already been armed in this page load. */
  private codeFlowStarted = false;

  private readonly oauthService = inject(OAuthService);
  private readonly oAuthConfig = inject(HYPECAST_OAUTH_CONFIG);
  private readonly tenantService = inject(ITenantService);
  private readonly authenticationService = inject(HypecastAuthenticationService);
  private readonly authFailure = inject(AuthFailureService);

  constructor() {
    this.oauthService.configure(this.oAuthConfig);
    this.initialize();
    this.setupVisibilityChangeHandler();
  }

  private applyClientId(clientId?: string): boolean {
    if (!clientId) {
      return false;
    }

    this.oAuthConfig.clientId = clientId;
    this.oauthService.clientId = clientId;

    return true;
  }

  private ensureClientIdConfigured(): Promise<void> {
    if (this.applyClientId(this.oauthService.clientId) || this.applyClientId(this.oAuthConfig.clientId)) {
      return Promise.resolve();
    }

    const currentTenant = this.tenantService.getTenant();
    if (this.applyClientId(currentTenant?.clientId)) {
      return Promise.resolve();
    }

    return this.tenantService.loadTenant().then((tenant) => {
      if (this.applyClientId(tenant?.clientId)) {
        return;
      }

      console.warn('Missing OAuth client_id: tenant configuration did not provide a clientId');
      throw {
        error: 'login_required',
        error_description: 'missing_client_id',
      };
    });
  }

  getIdentityClaims(): object {
    return this.oauthService.getIdentityClaims();
  }

  getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  /**
   * Returns the current access_token.
   */
  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  /**
   * Returns the current refresh_token.
   */
  getRefreshToken(): string {
    return this.oauthService.getRefreshToken();
  }

  /**
   * Returns the expiration date of the access_token
   * as milliseconds since 1970.
   */
  getAccessTokenExpiration(): number {
    return this.oauthService.getAccessTokenExpiration();
  }

  /**
   * Returns the expiration date of the id_token
   * as milliseconds since 1970.
   */
  getIdTokenExpiration(): number {
    return this.oauthService.getAccessTokenExpiration();
  }

  /**
   * Checkes, whether there is a valid access_token.
   */
  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Checks whether there is a valid id_token.
   */
  hasValidIdToken(): boolean {
    return this.oauthService.hasValidIdToken();
  }

  /**
   * Retrieve a saved custom property of the TokenReponse object. Only if predefined in authconfig.
   */

  logOut(): void {
    this.oauthService.logOut();
  }

  /**
   * Refreshes only the token without triggering full login flow.
   * Used by the authorization interceptor to handle 401 errors.
   */
  refreshTokenOnly(): Promise<boolean> {
    if (__.IsNullOrUndefinedOrEmpty(this.oauthService.getRefreshToken())) {
      // No refresh token available
      return Promise.resolve(false);
    }

    return this.refresh();
  }

  /**
   * Runs the login flow, joining an already running one instead of starting a
   * second. Two concurrent flows each write their own nonce and PKCE verifier into
   * the same storage, so whichever authorization code comes back can no longer be
   * redeemed — that is the race behind the blank page after the IdP redirect.
   *
   * Always returns a promise (callers chain `.then()` on it unconditionally).
   */
  login(): Promise<unknown> {
    if (window.location.href.includes('skipAuthentication')) {
      return Promise.resolve();
    }

    if (!__.IsNullOrUndefined(this.loginInFlight)) {
      return this.loginInFlight;
    }

    this.loginInFlight = this.runLoginFlow().finally(() => {
      this.loginInFlight = null;
    });

    return this.loginInFlight;
  }

  /**
   * "Mit anderem Konto anmelden" on the access-denied page. Ends the session at
   * the identity provider (its end-session endpoint from the discovery
   * document), which then sends the browser back to this app's origin; with
   * no tokens left, the app starts a fresh sign-in from there. Clearing local
   * state alone is not enough: the ID server session cookie would sign the
   * same account in again and get the same refusal.
   */
  async signInWithDifferentAccount(): Promise<void> {
    clearTransientLoginKeys();
    this.clearCallbackRetryMarker();

    await this.ensureClientIdConfigured().catch((): void => undefined);
    await this.ensureDiscoveryDocumentLoaded().catch((error: unknown) => {
      console.warn('Discovery document unavailable, using the default end-session endpoint', error);
    });

    if (!this.oauthService.logoutUrl) {
      this.oauthService.logoutUrl = fallbackEndSessionUrl(this.oAuthConfig.issuer || this.oauthService.issuer || '');
    }

    // logOut() navigates through openUri, which the denied callback disarmed.
    this.oAuthConfig.allowLoginRedirects();
    // There is no id_token to hint with, so client_id tells the ID server whose
    // post-logout redirect URIs (the same as its redirect URIs) to check.
    this.oauthService.logOut({
      client_id: this.oauthService.clientId,
      post_logout_redirect_uri: window.location.origin,
    });
  }

  private runLoginFlow(): Promise<unknown> {
    const deniedRedirect = readOAuthErrorRedirect(window.location.search);
    if (deniedRedirect !== null) {
      return this.stopAtDeniedRedirect(deniedRedirect);
    }

    let chain: Promise<unknown>;

    if (__.IsNullOrUndefined(this.tenantService.getTenant())) {
      chain = this.tenantService.loadTenant().then((q) => {
        this.applyClientId(q.clientId);
        return this.oauthService.loadDiscoveryDocument();
      });
    } else {
      this.applyClientId(this.tenantService.getTenant()?.clientId);
      chain = this.oauthService.loadDiscoveryDocument();
    }

    return chain
      .then((_q: unknown) => {
        if (!__.IsNullOrUndefinedOrEmpty(this.oauthService.getRefreshToken())) {
          // A refresh token is present, so try to refresh
          return this.refresh();
        }
        // No refresh token is present
        if (window.location.search.indexOf('code=') === -1) {
          // If no code flow has previously been initialized
          this.startCodeFlowOnce();
          return Promise.resolve<boolean>(false);
        } else {
          // If the code flow has previously been initialized: once the code exchange
          // runs, later refresh failures must not auto-redirect to the IdP anymore
          // (we wait for a manual re-login instead).
          this.oAuthConfig.suppressLoginRedirects();
          return this.oauthService
            .tryLogin()
            .then((result: boolean) => {
              // The callback went through, so the next one starts from scratch again.
              this.clearCallbackRetryMarker();
              return result;
            })
            .catch((error: unknown) => this.recoverFromFailedCallback(error))
            .finally(() => this.removeIssParameterFromLocation());
        }
      })
      .then((_q: unknown) => {
        if (_q === false) {
          // The operation has been unsuccessful
          // TODO: What should we do in this case?
          return Promise.reject(false);
        }
        // The operation was successful

        if (this.oauthService.hasValidAccessToken()) {
          // An access token is present
          return Promise.resolve(true);
        }

        // If all fails, try again a refresh
        return this.refresh();
      })
      .then(() => {
        // Make sure the flow has been completed properly
        if (this.oauthService.state && this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
          let stateUrl = this.oauthService.state;
          if (stateUrl.startsWith('/') === false) {
            stateUrl = decodeURIComponent(stateUrl);
          }
          if (stateUrl.startsWith('//')) {
            stateUrl = '/';
          }
          console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
        }
      })
      .then(() => {
        // Initialize any tasks from the authentication service
        this.authenticationService.init();
      })
      .then(() => {
        // Setup automatic token refresh now that discovery document is loaded
        this.setupAutomaticTokenRefresh();
      });
  }

  /**
   * The identity provider refused the sign-in and sent `?error=...` back. Starting
   * the code flow again would only loop: the ID server session cookie signs the
   * same account in and gets the same refusal. Stop here, clean the address bar
   * and let the app show the access-denied page.
   *
   * Rejects with `false` like a pending redirect, so initialize() does not log it as a crash.
   */
  private stopAtDeniedRedirect(redirect: OAuthErrorRedirect): Promise<never> {
    const cleanedHref = removeOAuthErrorParameters(window.location.href);
    if (cleanedHref !== window.location.href) {
      history.replaceState(null, window.name, cleanedHref);
    }
    clearTransientLoginKeys();
    this.clearCallbackRetryMarker();
    // Nothing may restart the code flow behind the page (refresh paths, interceptors).
    this.oAuthConfig.suppressLoginRedirects();
    this.authFailure.denyAccess(redirect);
    return Promise.reject(false);
  }

  /**
   * Sends the user to the identity provider at most once per page load.
   *
   * Every `initCodeFlow()` writes a fresh nonce and PKCE verifier over the previous
   * one, so a second call while the first redirect is still pending invalidates the
   * code the user is about to come back with. One redirect is all we ever need: the
   * navigation is already on its way.
   */
  private startCodeFlowOnce(): void {
    if (this.oAuthConfig.loginRedirectsSuppressed) {
      // openUri is disarmed, so this would not navigate anywhere anyway — but it
      // would still overwrite the verifier of the callback we are handling.
      return;
    }

    if (this.codeFlowStarted) {
      console.warn('A redirect to the identity provider is already pending, not starting a second code flow');
      return;
    }

    this.oauthService.initCodeFlow();
    // Only after the call: if it throws, no redirect was armed and a later retry
    // must still be allowed.
    this.codeFlowStarted = true;
  }

  /**
   * The identity provider handed us an authorization code we cannot redeem —
   * in practice `invalid_nonce_in_state`, because a second login flow overwrote the
   * nonce and PKCE verifier this code was bound to.
   *
   * Nothing downstream recovers from this on its own: the login chain rejects,
   * `suppressLoginRedirects()` has already disarmed `openUri`, and
   * HypecastAuthenticationService.init() is never reached, so `credentials$` stays
   * silent and the router leaves an empty outlet behind — the blank page users
   * reported. The identity provider session cookie exists at this point, so one
   * more round trip completes without any user interaction. This does automatically
   * what a manual page reload used to do.
   */
  private recoverFromFailedCallback(error: unknown): Promise<boolean> {
    if (sessionStorage.getItem(HypecastOAuthService.CALLBACK_RETRY_KEY) === '1') {
      // Second failure in a row: not a race, so stop and let the user see it.
      this.clearCallbackRetryMarker();
      this.authFailure.fail(error);
      return Promise.resolve(false);
    }

    console.warn('Could not complete the login callback, retrying the code flow once', error);
    sessionStorage.setItem(HypecastOAuthService.CALLBACK_RETRY_KEY, '1');
    clearTransientLoginKeys();
    this.oAuthConfig.allowLoginRedirects();
    this.startCodeFlowOnce();

    return Promise.resolve(false);
  }

  private clearCallbackRetryMarker(): void {
    sessionStorage.removeItem(HypecastOAuthService.CALLBACK_RETRY_KEY);
  }

  /**
   * The IdP appends an `iss` parameter (RFC 9207) to the redirect URL. After login,
   * angular-oauth2-oidc only removes code/scope/state/session_state from the URL,
   * so the leftover `iss` has to be stripped here (HYPE-1188).
   */
  private removeIssParameterFromLocation(): void {
    const cleanedHref = removeIssQueryParameter(window.location.href);
    if (cleanedHref !== window.location.href) {
      history.replaceState(null, window.name, cleanedHref);
    }
  }

  private initialize(): void {
    if (!this.oauthService.hasValidAccessToken()) {
      // No valid access token is present

      const loginResult = this.login();
      if (loginResult && typeof loginResult.catch === 'function') {
        loginResult.catch((error: unknown) => {
          // false means initCodeFlow() was called and a redirect to the IdP is pending — not an error
          if (error !== false) {
            console.error('OAuth initialization failed during login flow:', error);
          }
        });
      }
    } else {
      // An access token is present, initialize any tasks from the authentication service.
      // This will be skipped if the local storage already contains information
      // Load the discovery document BEFORE the first authenticated request goes out.
      // authenticationService.init() used to run first, so the page's own requests
      // (and the proactive refresh the interceptor fires for a token expiring within
      // 5 minutes) could race ahead of a document that was still in flight.
      //
      // The old code also returned early when the clientId was missing, which left the
      // document permanently unloaded for that page load. Hypecast.WebApp already does
      // it in this order and unconditionally.
      this.ensureClientIdConfigured()
        .catch((): void => undefined)
        .then(() => this.ensureDiscoveryDocumentLoaded())
        .catch((error: unknown) => {
          console.error('Failed to load discovery document during initialization', error);
        })
        .then(() => {
          this.authenticationService.init();
          this.setupAutomaticTokenRefresh();
        });
    }
  }

  /**
   * Sets up a visibility change handler to check and refresh token when tab becomes active.
   * This handles the case where setTimeout is throttled during tab inactivity.
   */
  private setupVisibilityChangeHandler(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Tab became visible - check if token needs refresh

        // Only proceed if discovery document is loaded
        if (!this.oauthService.discoveryDocumentLoaded) {
          return;
        }

        const expiresAt = this.oauthService.getAccessTokenExpiration();
        if (!expiresAt) {
          return;
        }

        const now = Date.now();
        const fiveMinutesFromNow = now + 5 * 60 * 1000;

        // If token is expired or will expire soon, refresh it
        if (expiresAt < fiveMinutesFromNow && !__.IsNullOrUndefinedOrEmpty(this.oauthService.getRefreshToken())) {
          console.log('Tab became active and token is expired or will expire soon. Refreshing...');
          this.refreshAndScheduleNext();
        }
      }
    });
  }

  /**
   * Sets up automatic token refresh before the access token expires.
   * Refreshes 5 minutes before expiration or at 80% of token lifetime (whichever is earlier).
   */
  private setupAutomaticTokenRefresh(): void {
    // Clear any existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    // Only setup automatic refresh if discovery document is loaded
    if (!this.oauthService.discoveryDocumentLoaded) {
      console.log('Discovery document not yet loaded, skipping automatic refresh setup');
      return;
    }

    if (!this.oauthService.hasValidAccessToken()) {
      console.log('No valid access token, skipping automatic refresh setup');
      return;
    }

    const expiresAt = this.oauthService.getAccessTokenExpiration();
    if (!expiresAt) {
      console.warn('Cannot determine token expiration, skipping automatic refresh');
      return;
    }

    const now = Date.now();
    const expiresIn = expiresAt - now;

    if (expiresIn <= 0) {
      console.log('Token already expired, triggering immediate refresh');
      this.refreshAndScheduleNext();
      return;
    }

    // Refresh at 80% of token lifetime or 5 minutes before expiration (whichever comes first)
    const refreshBeforeExpiry = Math.min(5 * 60 * 1000, expiresIn * 0.2); // 5 min or 20% of lifetime
    const refreshAt = expiresIn - refreshBeforeExpiry;

    console.log(
      `Token expires in ${Math.round(expiresIn / 1000 / 60)} minutes. Will refresh in ${Math.round(refreshAt / 1000 / 60)} minutes.`,
    );

    this.tokenRefreshTimer = setTimeout(() => {
      this.refreshAndScheduleNext();
    }, refreshAt);
  }

  /**
   * Refreshes the token and schedules the next automatic refresh.
   */
  private refreshAndScheduleNext(): void {
    console.log('Attempting automatic token refresh...');

    if (!__.IsNullOrUndefinedOrEmpty(this.oauthService.getRefreshToken())) {
      // refresh() loads the discovery document itself now, so the duplicate
      // load-then-refresh dance that used to live here is gone.
      this.refresh()
        .then((success) => {
          if (success) {
            console.log('Token refresh successful');
            // Schedule next refresh
            this.setupAutomaticTokenRefresh();
          } else {
            console.warn('Token refresh was not successful but did not throw error');
          }
        })
        .catch((error) => {
          console.error('Automatic token refresh failed', error);
          // Don't schedule next refresh on failure - let the interceptor handle it
        });
    } else {
      console.warn('No refresh token available for automatic refresh');
    }
  }

  /**
   * `tokenEndpoint` is only ever populated from the discovery document, so a refresh
   * attempted before that document is loaded throws
   * "'tokenEndpoint' should not be null" from deep inside angular-oauth2-oidc.
   *
   * Three other refresh paths already guard on this (the visibilitychange handler,
   * setupAutomaticTokenRefresh and refreshAndScheduleNext) — refresh() itself did not,
   * and it is the one the authorization interceptor calls.
   */
  private ensureDiscoveryDocumentLoaded(): Promise<void> {
    if (this.oauthService.discoveryDocumentLoaded) {
      return Promise.resolve();
    }
    return this.oauthService.loadDiscoveryDocument().then((): void => undefined);
  }

  private refresh(): Promise<boolean> {
    return this.ensureClientIdConfigured()
      .then(() => this.ensureDiscoveryDocumentLoaded())
      .then(() => this.oauthService.refreshToken())
      .then(() => {
        // Schedule next automatic refresh after successful refresh
        this.setupAutomaticTokenRefresh();
        return Promise.resolve(true);
      })
      .catch((result: unknown) => {
        // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
        const errorResponsesRequiringUserInteraction = [
          'interaction_required',
          'token_refresh_error',
          'login_required',
          'account_selection_required',
          'consent_required',
          'refresh token',
        ];

        const errorResult = result as {
          error?: { error?: string; error_description?: string } | string;
          error_description?: string;
          message?: string;
          status?: number;
        };
        const rawErrorCode =
          (typeof errorResult.error === 'object' &&
            errorResult.error !== null &&
            typeof errorResult.error.error === 'string' &&
            errorResult.error.error) ||
          (typeof errorResult.error === 'string' && errorResult.error) ||
          '';
        const rawErrorDescription =
          (typeof errorResult.error === 'object' &&
            errorResult.error !== null &&
            typeof errorResult.error.error_description === 'string' &&
            errorResult.error.error_description) ||
          (typeof errorResult.error_description === 'string' && errorResult.error_description) ||
          (typeof errorResult.message === 'string' && errorResult.message) ||
          '';
        const normalizedErrorText = `${rawErrorCode} ${rawErrorDescription}`.toLowerCase();

        if (errorResponsesRequiringUserInteraction.some((q) => normalizedErrorText.includes(q))) {
          // Logout to restart the flow
          this.oauthService.logOut(false);

          // Restart the code flow
          this.startCodeFlowOnce();

          console.warn('User interaction is needed to log in, we will wait for the user to manually log in.');
          return Promise.resolve(false);
        }

        // Everything that reaches this point used to be called "unknown" and answered
        // with a hard logout. Over a month that lumped together two very different
        // failures across 8 tenants: 6 config races (the tokenEndpoint case above) and
        // 5 genuine rejections from the token endpoint. Only the second kind is a
        // verdict on our refresh token.
        //
        // Distinguish them structurally rather than by message text: a rejection from
        // the token endpoint carries an HTTP status or an OAuth error code. Anything
        // without either never reached the server, so the session is still good and
        // logging the user out would cost them their place for nothing.
        const httpStatus = typeof errorResult.status === 'number' ? errorResult.status : null;
        const serverRejected = httpStatus !== null || rawErrorCode !== '';

        if (!serverRejected) {
          // Transient / client-side. Keep the session and let the next request retry;
          // if the refresh token really is dead, the retry comes back with a status and
          // takes the branch below.
          console.error('Token refresh failed before reaching the token endpoint, keeping the session:', result);
          return Promise.resolve(false);
        }

        console.error(
          `Token refresh rejected by the token endpoint (status=${httpStatus ?? 'n/a'}, ` +
            `code=${rawErrorCode || 'n/a'}), redirecting to login:`,
          result,
        );
        this.oauthService.logOut(false);
        this.startCodeFlowOnce();
        return Promise.resolve(false);
      });
  }
}
